<template>
  <div class="predictive-search-results grid">
    <div class="col xs-span l4">
      <div class="predictive-search-results__blocks">
        <transition name="fade">
          <div
            v-if="suggestions.length"
            class="predictive-search-results__block"
          >
            <p class="predictive-search-results__title text-heading-m">
              <strong
                class="text-heading-m"
                v-text="$string('predictive_search.suggestions')"
              />
            </p>

            <ul class="predictive-search-results__list">
              <li
                v-for="suggestion of suggestions.slice(0, 3)"
                :key="suggestion"
              >
                <a
                  :href="generateSuggestionLink(suggestion)"
                  class="text-body-m-bold text-body-s-bold-desktop"
                  v-html="highlightText(suggestion.text)"
                />
              </li>
            </ul>
          </div>
        </transition>

        <transition name="fade">
          <div
            v-if="isMobile && products.length"
            class="predictive-search-results__block"
          >
            <p class="predictive-search-results__title text-heading-m">
              <strong
                class="text-heading-m"
                v-text="$string('predictive_search.products')"
              />
            </p>

            <ul
              class="
                predictive-search-results__list
                predictive-search-results__product-list
              "
            >
              <li
                v-for="product of products.slice(0, 4)"
                :key="product.handle"
              >
                <product-card-simple
                  :product="product"
                />
              </li>
            </ul>
          </div>
        </transition>

        <transition name="fade">
          <div
            v-if="collections.length"
            class="predictive-search-results__block"
          >
            <p class="predictive-search-results__title text-heading-m">
              <strong
                class="text-heading-m"
                v-text="$string('predictive_search.collections')"
              />
            </p>

            <ul class="predictive-search-results__list">
              <li
                v-for="collection of collections.slice(0, 3)"
                :key="collection.handle"
              >
                <a
                  :href="collection.url"
                  class="text-body-m-bold text-body-s-bold-desktop"
                  v-html="highlightText(collection.title)"
                />
              </li>
            </ul>
          </div>
        </transition>

        <transition name="fade">
          <div
            v-if="pages.length"
            class="predictive-search-results__block"
          >
            <p class="predictive-search-results__title text-heading-m">
              <strong
                class="text-heading-m"
                v-text="$string('predictive_search.pages')"
              />
            </p>

            <ul class="predictive-search-results__list">
              <li
                v-for="page of pages.slice(0, 3)"
                :key="page.handle"
              >
                <a
                  :href="page.onlineStoreUrl"
                  class="text-body-m-bold text-body-s-bold-desktop"
                  v-html="highlightText(page.title)"
                />
              </li>
            </ul>
          </div>
        </transition>

        <transition name="fade">
          <div
            v-if="articles.length"
            class="predictive-search-results__block"
          >
            <p class="predictive-search-results__title text-heading-m">
              <strong
                class="text-heading-m"
                v-text="$string('predictive_search.articles')"
              />
            </p>

            <ul class="predictive-search-results__list">
              <li
                v-for="article of articles.slice(0, 3)"
                :key="article.handle"
              >
                <a
                  :href="article.onlineStoreUrl"
                  class="text-body-m-bold text-body-s-bold-desktop"
                  v-html="highlightText(article.title)"
                />
              </li>
            </ul>
          </div>
        </transition>
      </div>
    </div>

    <transition name="fade">
      <div
        v-if="!isMobile && products.length"
        class="col xs-span l6-12"
      >
        <p class="predictive-search-results__title text-heading-m">
          <strong
            class="text-heading-m"
            v-text="$string('predictive_search.products')"
          />
        </p>

        <ul
          class="
            predictive-search-results__list
            predictive-search-results__product-list
          "
        >
          <li
            v-for="product of products.slice(0, 4)"
            :key="product.handle"
          >
            <product-card-simple
              :product="product"
            />
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>

/**
 * Vue: Predictive search results (predictive-search-results)
 * -----------------------------------------------------------------------------
 * Component to display predictive search results.
 *
 * @param {Array} articles - Array of article search results.
 * @param {Array} collections - Array of collection search results.
 * @param {Array} pages - Array of page search results.
 * @param {Array} products - Array of product search results.
 * @param {Array} suggestions - Array of suggestions from predictive search.
 * @param {String} searchQuery - Current search query.
 *
 */
import { mapState } from 'vuex'

import ProductCardSimple from '~global/product-card-simple/product-card-simple'

export default {
  name: 'PredictiveSearchResults',

  components: {
    ProductCardSimple,
  },

  props: {
    articles: {
      type: Array,
      default: () => [],
    },

    collections: {
      type: Array,
      default: () => [],
    },

    pages: {
      type: Array,
      default: () => [],
    },

    products: {
      type: Array,
      default: () => [],
    },

    suggestions: {
      type: Array,
      default: () => [],
    },

    searchQuery: {
      type: String,
      default: '',
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  methods: {

    /**
     * Returns highlighted text from the recommendation string.
     * @returns {String}
     */
    highlightText(text) {
      return text.replace(
        this.searchQuery,
        `<span class="text-body-m-regular text-body-s-regular-desktop">${this.searchQuery}</span>`,
      )
    },

    /**
     * Returns a link from suggestion text and tracking parameters.
     * @returns {String}
     */
    generateSuggestionLink(suggestion) {
      return `${this.$variable('routes.search')}?q=${suggestion.text}&${suggestion.trackingParameters}`
    },
  },
}
</script>
